import Iconize from 'app/shared/Alfacom/Icon/Iconize'
import axios, {AxiosError, AxiosResponse} from 'axios'
import createAuthRefreshInterceptor, {AxiosAuthRefreshRequestConfig} from 'axios-auth-refresh'
import {useFormik} from 'formik'
import {FC, ReactNode, useEffect, useState} from 'react'
import {Alert, Button, Col, Form, Modal, Row} from 'react-bootstrap'
import * as yup from 'yup'
import {UNAUTHORIZED_REASON, VALIDATION, VALIDATION_ERROR} from '../../shared/Common/Constants'
import FormikErrors from '../../shared/FormikErrors/FormikErrors'
import {getAuth, removeAuth, setAuth, useAuth} from '../auth'
import ChangePassword from '../auth/components/ChangePassword'
import {GET_USER_BY_ACCESSTOKEN_URL, login, reauthWithRefreshToken} from '../auth/core/_requests'

export interface AxiosInterceptorProps {
  children?: ReactNode
}

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .max(VALIDATION.PASSWORD.LOGIN.MAX, VALIDATION_ERROR.STRING.MAX(VALIDATION.PASSWORD.LOGIN.MAX))
    .required(VALIDATION_ERROR.STRING.REQUIREDx('Password')),
})

var savedResolve: (value: any) => void
var savedReject: (error: any) => void

const AxiosInterceptor: FC<AxiosInterceptorProps> = ({children}) => {
  var isGettingUserInputCredentials: boolean = false
  const [isCalled, setIsCalled] = useState(false)
  const [showCredPrompt, setShowCredPrompt] = useState(false)
  const [isReauthSubmitting, setIsReauthSubmitting] = useState(false)
  const [reauthError, setReauthError] = useState<string | undefined>(undefined)
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [successPasswordChanged, setSuccessPasswordChanged] = useState(false)
  const {currentUser, saveAuth, logout} = useAuth()
  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus}) => {
      setIsReauthSubmitting(true)
      savedResolve(values)
    },
  })

  const handleCloseCredPrompt = () => {
    if (!isReauthSubmitting) {
      savedReject('Reauth cancelled')
    }
  }

  const getUserInputCredentials = (): Promise<any> =>
    new Promise((resolve, reject) => {
      setShowCredPrompt(true)
      savedResolve = resolve
      savedReject = reject
    })

  const tryReauthWithUserInputCredentials = () =>
    new Promise(async (resolve) => {
      const maxReauth = 3
      let reauthCount = 0
      isGettingUserInputCredentials = true
      do {
        await getUserInputCredentials()
          .then(async ({username, password}) => {
            try {
              reauthCount++
              setReauthError(undefined)
              const {data: auth} = await login(username, password)
              saveAuth(auth)
              setIsReauthSubmitting(false)
              setShowCredPrompt(false)
              isGettingUserInputCredentials = false
              resolve(true)
            } catch (error) {
              if (axios.isAxiosError(error) && error.response) {
                setReauthError(error.response.data.error_description)
              } else {
                setReauthError(`Login details are incorrect.`)
              }
              setIsReauthSubmitting(false)
              formik.setFieldValue('password', '', false)
              if (reauthCount >= maxReauth) {
                setIsReauthSubmitting(false)
                setShowCredPrompt(false)
                isGettingUserInputCredentials = false
                resolve(false)
              }
            }
          })
          .catch((err) => {
            // User pressed cancel, resolve false
            setIsReauthSubmitting(false)
            setShowCredPrompt(false)
            isGettingUserInputCredentials = false
            resolve(false)
          })
      } while (isGettingUserInputCredentials && reauthCount < maxReauth)

      resolve(false)
    })

  const getUserUpdatePassword = (): Promise<any> =>
    new Promise((resolve, reject) => {
      setShowPasswordPrompt(true)
      savedResolve = resolve
      savedReject = reject
    })

  const handleCloseUpdatePassword = () => {
    savedResolve(true)
  }

  const handleUserPasswordUpdated = () => {
    setSuccessPasswordChanged(true)
    removeAuth()
  }

  const promptUpdatePassword = () =>
    new Promise(async (resolve) => {
      isGettingUserInputCredentials = true
      do {
        await getUserUpdatePassword()
          .then(async (result) => {
            try {
              setShowPasswordPrompt(false)
              setSuccessPasswordChanged(false)
              isGettingUserInputCredentials = false
              resolve(true)
            } catch (error) {
              console.error(error)
              resolve(false)
            }
          })
          .catch((err) => {
            setShowPasswordPrompt(false)
            setSuccessPasswordChanged(false)
            isGettingUserInputCredentials = false
            resolve(false)
          })
      } while (isGettingUserInputCredentials)

      resolve(false)
    })

  useEffect(() => {
    formik.resetForm()
    if (currentUser) {
      formik.setFieldValue('username', currentUser.username)
    }
  }, [currentUser])

  useEffect(() => {
    if (!isCalled) {
      axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        Accept: 'application/json',
      }
      axios.defaults.withCredentials = true

      axios.interceptors.response.use(
        (response: AxiosResponse) => response,
        async (error: AxiosError) => {
          const config: AxiosAuthRefreshRequestConfig = error.config
          // 401 Unauthorized - Password expired => prompt update password and logout immediately
          if (error.response && error.response.status === 401 && error.response.statusText === UNAUTHORIZED_REASON.PASSWORD_EXPIRED) {
            var result = await promptUpdatePassword()
            if (result) {
              logout()
              return Promise.resolve()
            }
          }
          return Promise.reject(error)
        }
      )

      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          const auth = getAuth()
          if (auth && auth.access_token) {
            config.headers = {
              ...config.headers,
              Authorization: `Bearer ${auth.access_token}`,
            }
          }

          return config
        },
        (err: any) => Promise.reject(err)
      )

      const refreshAuth = async (failedRequest: any) => {
        const auth = getAuth()
        if (auth && auth.access_token && auth.refresh_token && failedRequest.response.statusText !== UNAUTHORIZED_REASON.PASSWORD_EXPIRED) {
          try {
            removeAuth()
            // Try getting a new access token using the refresh token
            const {data: newAuth} = await reauthWithRefreshToken(auth.refresh_token)

            if (newAuth && newAuth.access_token) {
              // If successfully refreshed access token, retry request
              setAuth(newAuth)
              failedRequest.response.config.headers = {
                ...failedRequest.response.config.headers,
                Authorization: `Bearer ${newAuth.access_token}`,
              }
              return Promise.resolve()
            }
          } catch (_error: any) {
            const samePageReauthEnabled = process.env.REACT_APP_SAME_PAGE_REAUTH === 'true'
            if (samePageReauthEnabled && failedRequest.response.config.url !== GET_USER_BY_ACCESSTOKEN_URL) {
              // User is already logged in, but is not authorized when making an API request and has failed to
              // refresh their access token.
              //
              // So, we prompt the user to reenter their credentials to get a new access token
              // Then, we retry the request if a new access token is successfully retrieved
              // Otherwise, log out the user
              var reauthSuccess = await tryReauthWithUserInputCredentials()
              if (reauthSuccess) {
                // Reauthed, get latest access token and retry request
                const reauthedAuth = getAuth()
                if (reauthedAuth && reauthedAuth.access_token) {
                  failedRequest.response.config.headers = {
                    ...failedRequest.response.config.headers,
                    Authorization: `Bearer ${reauthedAuth.access_token}`,
                  }
                  return Promise.resolve()
                }
              }
            }

            logout()
            return Promise.reject(_error)
          }
        }
        return Promise.reject(failedRequest)
      }

      createAuthRefreshInterceptor(axios, refreshAuth)
      setIsCalled(true)

      return () => {
        axios.interceptors.request.eject(requestInterceptor)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Modal size='sm' className='front' backdropClassName='front' show={showCredPrompt} onHide={handleCloseCredPrompt} centered backdrop='static'>
        <Modal.Header closeButton={!isReauthSubmitting}>
          <Modal.Title>Re-enter Credentials</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reauthError && (
            <Alert variant='danger' className='mb-10'>
              {reauthError}
            </Alert>
          )}
          <Row className='g-6'>
            <Col xs={12}>
              <div className='mb-6'>Your session has expired, please re-enter your password to continue.</div>
            </Col>
          </Row>
          <Row className='g-6'>
            <Col xs={12}>
              <Form noValidate id='reauth_form'>
                <Form.Group>
                  <Form.Label className={`fw-bold fs-6`}>Username</Form.Label>
                  <Col className='d-flex flex-column gap-2'>{formik.values.username}</Col>
                </Form.Group>
                <Form.Group>
                  <Form.Label className={`fw-bold fs-6`}>Password</Form.Label>
                  <Col className='d-flex flex-column gap-2'>
                    <Form.Control
                      {...formik.getFieldProps('password')}
                      type='password'
                      placeholder='Password'
                      maxLength={50}
                      disabled={isReauthSubmitting}
                      autoComplete='off'
                    />

                    <FormikErrors formik={formik} fieldKey='password' />
                  </Col>
                </Form.Group>
                <Form.Group className='border border-bottom-0 border-left-0 border-right-0 mx-n7 mb-n7 p-7'>
                  <div className='d-flex justify-content-center gap-2'>
                    <Button type='submit' className='flex-grow-1' onClick={formik.submitForm} disabled={isReauthSubmitting}>
                      Sign In
                    </Button>
                    <Button
                      type='button'
                      variant='light-danger'
                      className='flex-grow-1'
                      onClick={handleCloseCredPrompt}
                      disabled={isReauthSubmitting}
                    >
                      Log Out
                    </Button>
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <Modal
        show={showPasswordPrompt}
        onHide={() => {
          setShowPasswordPrompt(false)
        }}
        centered
        backdrop='static'
      >
        <Modal.Header>
          <Modal.Title>
            {successPasswordChanged && 'Password Updated Successfully'}
            {!successPasswordChanged && 'Change Password'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='min-h-150px'>
          <Row className='g-6'>
            {successPasswordChanged && <p>Your password has been changed successfully. You must login again to use the system.</p>}
            {!successPasswordChanged && (
              <ChangePassword
                show={!successPasswordChanged}
                isModal={true}
                showCancel={false}
                onSuccessSave={handleUserPasswordUpdated}
                keepCurrentAuthAlive={false}
                subMessage='Your current password has expired and you have been logged out by system automatically, all unsaved changes are lost. You must change your password now in order to use the system.'
              />
            )}
          </Row>
        </Modal.Body>
        {successPasswordChanged && (
          <Modal.Footer className='d-flex flex-wrap gap-2 justify-content-end'>
            {successPasswordChanged && (
              <>
                <Button variant='light-danger' onClick={handleCloseUpdatePassword}>
                  <Iconize biIcon={`bi bi-escape`} position='start' />
                  Logout
                </Button>
              </>
            )}
          </Modal.Footer>
        )}
      </Modal>
      {isCalled && children}
    </>
  )
}

export {AxiosInterceptor}
